import { useEffect } from 'react';
import { useNavigate } from 'react-router';

interface IUsePreventBackNavigationOptions {
  targetKickUrl: string;
}

/**
 * Custom hook to prevent browser back button navigation and redirect to a specified URL.
 *
 * This hook is useful when you want to prevent users from navigating back to a previous step
 * in a multi-step form or wizard using the browser's back button, while still allowing
 * programmatic navigation via React Router.
 *
 * @example
 * ```tsx
 * const ReviewPage = () => {
 *   usePreventBackNavigation({ targetKickUrl: '/permanent-jobs/postings' });
 *   return <div>Review your information...</div>;
 * };
 * ```
 *
 * @param options - Configuration options
 * @param options.targetKickUrl - The URL to redirect to when back navigation is detected
 */
export const usePreventBackNavigation = ({ targetKickUrl }: IUsePreventBackNavigationOptions) => {
  const navigate = useNavigate();

  useEffect(() => {
    // Push a dummy state to create a history entry
    // This allows us to intercept the back button press.
    //
    // Preserve the current state (notably the `idx` set by the `history`
    // library) — pushing `null` would strip `idx` off this entry, and when
    // the user later POPs back to it from a downstream page, history v5
    // sees `nextIndex == null` and silently bails on its blocker logic
    // ("POP to a location with no index"). That breaks every navigation
    // blocker further down the stack (e.g. the Unsaved Changes popup on
    // the edit-onsite-interview page).
    const currentState = window.history.state;
    window.history.pushState(currentState, '', window.location.href);

    const handlePopState = () => {
      // When user clicks browser back button, redirect them to the target URL
      // Use replace: true to avoid creating another history entry
      navigate(targetKickUrl, { replace: true });
    };

    // Listen for popstate events (triggered by back/forward browser buttons)
    window.addEventListener('popstate', handlePopState);

    // Cleanup: remove event listener when component unmounts
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate, targetKickUrl]);
};
